import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, TrendingUp, Activity, Clock, ChevronRight, RefreshCw, Download } from "lucide-react";
import api from "../api/client";
import { AdminTabs } from "./AdminDashboard";
import { formatNumber } from "../lib/format";

export default function AdminFees() {
    const [data, setData] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [coinFilter, setCoinFilter] = useState("");
    const [downloading, setDownloading] = useState(false);

    const load = async () => {
        setRefreshing(true);
        try {
            const url = coinFilter
                ? `/admin/fees/collected?limit=300&coin=${encodeURIComponent(coinFilter)}`
                : "/admin/fees/collected?limit=300";
            const r = await api.get(url);
            setData(r.data);
        } catch { /* swallow — error toast handled by interceptor */ } finally {
            setRefreshing(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [coinFilter]);

    const downloadCsv = async () => {
        setDownloading(true);
        try {
            const url = coinFilter
                ? `/admin/fees/collected.csv?coin=${encodeURIComponent(coinFilter)}`
                : "/admin/fees/collected.csv";
            const r = await api.get(url, { responseType: "blob" });
            const blob = new Blob([r.data], { type: "text/csv" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            const today = new Date().toISOString().slice(0, 10);
            link.download = `verslan-fees-${today}${coinFilter ? `-${coinFilter}` : ""}.csv`;
            link.click();
            URL.revokeObjectURL(link.href);
        } catch { /* swallow */ } finally {
            setDownloading(false);
        }
    };

    if (!data) {
        return (
            <div className="min-h-[60vh] grid place-items-center font-mono text-xs text-zinc-500">
                Loading fees ledger…
            </div>
        );
    }

    const t = data.totals;
    return (
        <div className="bg-[#050505] min-h-[calc(100vh-3.5rem)] bg-grid-fine">
            <div className="max-w-[1480px] mx-auto px-4 lg:px-8 py-10 grid gap-6">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400">Admin · Fee ledger</div>
                    <h1 className="text-3xl font-medium tracking-tighter mt-2">Fees collected per trade</h1>
                    <p className="mt-1.5 text-[12px] text-zinc-500 max-w-2xl leading-relaxed">
                        Every fee event the protocol has captured — entry fees (per side at match), settlement service fees (per side at expiry), and liquidation fees. Click any row to drill into the trade.
                    </p>
                </div>
                <AdminTabs />

                {/* ---- Headline totals ---- */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2" data-testid="admin-fees-totals">
                    <Tile icon={Coins} label="Grand total"
                          value={`$${formatNumber(t.grand_total)}`}
                          sub={`across ${data.matched_swaps_total.toLocaleString()} swaps`} />
                    <Tile icon={TrendingUp} label="Entry fees" value={`$${formatNumber(t.entry)}`}
                          sub="per side · at match" />
                    <Tile icon={Activity} label="Settlement fees" value={`$${formatNumber(t.settlement)}`}
                          sub="per side · at expiry · 2× per swap" />
                    <Tile icon={Clock} label="Last 24h"
                          value={`$${formatNumber(t.last_24h.grand_total)}`}
                          sub={`entry $${formatNumber(t.last_24h.entry)} · settle $${formatNumber(t.last_24h.settlement)} · liq $${formatNumber(t.last_24h.liquidation)}`}
                          accent="text-cyan-300" />
                </div>

                {/* ---- By-duration breakdown ---- */}
                <div className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm p-5">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div>
                            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">Entry fees by duration</div>
                            <div className="text-sm font-medium mt-1">Where the protocol earns most</div>
                        </div>
                        <button onClick={load} disabled={refreshing} data-testid="admin-fees-refresh"
                                className="flex items-center gap-1.5 text-[11px] font-mono text-zinc-400 hover:text-cyan-300 disabled:opacity-40">
                            <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} /> Refresh
                        </button>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2 mt-4">
                        {(t.by_duration || []).map((r) => (
                            <div key={r.duration_key} className="border border-[#1F1F24] rounded-sm p-3"
                                 data-testid={`admin-fees-duration-${r.duration_key}`}>
                                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">{r.duration_key}</div>
                                <div className="text-lg font-medium tabular-nums mt-1">${formatNumber(r.total)}</div>
                                <div className="text-[10px] font-mono text-zinc-600 mt-0.5">{r.events.toLocaleString()} events</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ---- Ledger ---- */}
                <div className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm overflow-hidden">
                    <div className="px-5 py-3 flex items-center justify-between flex-wrap gap-3 border-b border-[#1F1F24]">
                        <div>
                            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">Live ledger</div>
                            <div className="text-sm font-medium mt-1">Per-trade fee events <span className="text-zinc-500">({data.items.length} most recent)</span></div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                data-testid="admin-fees-csv-download"
                                onClick={downloadCsv}
                                disabled={downloading}
                                className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-cyan-300 hover:text-cyan-200 border border-cyan-500/30 hover:border-cyan-400 px-3 py-1.5 rounded-sm disabled:opacity-40"
                            >
                                <Download className={`w-3 h-3 ${downloading ? "animate-pulse" : ""}`} />
                                {downloading ? "…" : "Download CSV"}
                            </button>
                            <label className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">Coin</label>
                            <input
                                data-testid="admin-fees-coin-filter"
                                value={coinFilter}
                                onChange={(e) => setCoinFilter(e.target.value.toUpperCase())}
                                placeholder="ALL"
                                className="bg-transparent border-b border-[#1F1F24] focus:border-cyan-400 outline-none font-mono text-sm w-24 text-zinc-100 py-1"
                            />
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-[12px] font-mono">
                            <thead>
                                <tr className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 border-b border-[#1F1F24]">
                                    <th className="text-left py-2 px-4">Time (UTC)</th>
                                    <th className="text-left py-2 px-4">Type</th>
                                    <th className="text-left py-2 px-4">Coin · Duration</th>
                                    <th className="text-right py-2 px-4">Per side</th>
                                    <th className="text-right py-2 px-4">Total (this row)</th>
                                    <th className="text-left py-2 px-4">Swap</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.items.map((r, i) => (
                                    <tr key={`${r.ts}-${i}`} className="border-b border-[#1F1F24]/60 hover:bg-cyan-500/[0.03]"
                                        data-testid={`admin-fees-row-${i}`}>
                                        <td className="py-2 px-4 text-zinc-400 tabular-nums whitespace-nowrap">
                                            {r.ts ? r.ts.replace("T", " ").slice(0, 19) : "—"}
                                        </td>
                                        <td className="py-2 px-4">
                                            <FeeTypePill type={r.fee_type} />
                                        </td>
                                        <td className="py-2 px-4 text-zinc-200">
                                            {r.coin || "—"}
                                            {r.duration_key && <span className="text-zinc-500"> · {r.duration_key}</span>}
                                        </td>
                                        <td className="py-2 px-4 text-right tabular-nums text-cyan-300">
                                            ${formatNumber(r.per_side)}
                                        </td>
                                        <td className="py-2 px-4 text-right tabular-nums text-zinc-100">
                                            ${formatNumber(r.amount)}
                                        </td>
                                        <td className="py-2 px-4">
                                            {r.swap_id ? (
                                                <Link to={`/admin/fees/swap/${r.swap_id}`}
                                                      className="text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-0.5"
                                                      data-testid={`admin-fees-swap-link-${r.swap_id.slice(0, 8)}`}>
                                                    {r.swap_id.slice(0, 8)}…
                                                    <ChevronRight className="w-3 h-3" />
                                                </Link>
                                            ) : "—"}
                                        </td>
                                    </tr>
                                ))}
                                {data.items.length === 0 && (
                                    <tr><td colSpan={6} className="text-center py-10 text-zinc-600">No fee events yet.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="text-[10px] font-mono text-zinc-600 leading-relaxed">
                    Service-fee policy: <span className="text-zinc-400">both winner and loser pay each per-side rate</span>. Total protocol take per swap = 2 × per-side fee. Liquidation fees are taken from the loser's forfeited margin only.
                </div>
            </div>
        </div>
    );
}

function Tile({ icon: Icon, label, value, sub, accent = "text-zinc-100" }) {
    return (
        <div className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm p-4 grid gap-1">
            <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">
                <Icon className="w-3 h-3" /> {label}
            </div>
            <div className={`text-2xl font-medium tracking-tight tabular-nums ${accent}`}>{value}</div>
            {sub && <div className="text-[10px] font-mono text-zinc-600 leading-relaxed">{sub}</div>}
        </div>
    );
}

function FeeTypePill({ type }) {
    const theme = {
        ENTRY:       "border-cyan-500/40 bg-cyan-500/5 text-cyan-300",
        SETTLEMENT:  "border-emerald-500/40 bg-emerald-500/5 text-emerald-300",
        LIQUIDATION: "border-rose-500/40 bg-rose-500/5 text-rose-300",
    }[type] || "border-zinc-700 text-zinc-300";
    return (
        <span className={`inline-block text-[9px] font-mono uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm border ${theme}`}>
            {type}
        </span>
    );
}
